import React, { useEffect, useState } from 'react';
import api from '../api';
import { RefreshCw, ShieldAlert, Mail, AlertOctagon, Filter, Inbox, ShieldCheck, ShieldX, Plus, X, Ban } from 'lucide-react';

const ACTION_COLORS = {
  'reject':         { color: '#ef4444', label: 'Rejeitado',     icon: 'err' },
  'soft reject':    { color: '#f97316', label: 'Soft reject',   icon: 'warn' },
  'rewrite subject':{ color: '#a855f7', label: 'Subject ⚠',    icon: 'warn' },
  'add header':     { color: '#3b82f6', label: 'Header ⚠',     icon: 'warn' },
  'greylist':       { color: '#06b6d4', label: 'Greylist',      icon: 'warn' },
  'no action':      { color: '#10b981', label: 'Aceito',        icon: 'ok' },
};

export default function Rspamd() {
  const [stat, setStat] = useState(null);
  const [history, setHistory] = useState([]);
  const [errors, setErrors] = useState([]);
  const [actions, setActions] = useState(null);
  const [lists, setLists] = useState(null);
  const [blocked, setBlocked] = useState({ recent: [], domains: [], total: 0 });
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState(null);

  const flash = (kind, text) => { setToast({ kind, text }); setTimeout(() => setToast(null), 5000); };

  const loadLists = async () => {
    try { const r = await api.get('/rspamd/lists'); setLists(r.data); } catch {}
  };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [s, h, e, a, l, b] = await Promise.allSettled([
        api.get('/rspamd/stat'),
        api.get('/rspamd/history'),
        api.get('/rspamd/errors'),
        api.get('/rspamd/actions'),
        api.get('/rspamd/lists'),
        api.get('/rspamd/blocked'),
      ]);
      if (s.status === 'fulfilled') setStat(s.value.data);
      if (h.status === 'fulfilled') setHistory(Array.isArray(h.value.data?.rows) ? h.value.data.rows : (h.value.data || []));
      if (e.status === 'fulfilled') setErrors(e.value.data || []);
      if (a.status === 'fulfilled') setActions(a.value.data);
      if (l.status === 'fulfilled') setLists(l.value.data);
      if (b.status === 'fulfilled') setBlocked(b.value.data || { recent: [], domains: [], total: 0 });
      if (s.status === 'rejected') setErr(s.reason?.response?.data?.error || 'Rspamd inacessível');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  const addToList = async (listKey, value) => {
    if (!value.trim()) return;
    try { await api.post(`/rspamd/lists/${listKey}`, { value: value.trim() }); flash('ok', `Adicionado: ${value.trim()}`); loadLists(); }
    catch (e) { flash('err', e.response?.data?.error || 'Erro ao adicionar'); }
  };
  const removeFromList = async (listKey, value) => {
    try { await api.delete(`/rspamd/lists/${listKey}`, { data: { value } }); flash('ok', `Removido: ${value}`); loadLists(); }
    catch (e) { flash('err', e.response?.data?.error || 'Erro ao remover'); }
  };

  if (err) return (
    <>
      <div className="page-title"><h1>Rspamd · Anti-Spam</h1></div>
      <div className="err-msg">{err}</div>
      <button onClick={load} className="ghost"><RefreshCw size={14}/> Tentar de novo</button>
    </>
  );

  const total = stat?.scanned || 0;
  const actionsCount = stat?.actions || {};
  const rejected = actionsCount['reject'] || 0;
  const passed = actionsCount['no action'] || 0;
  const uptime = stat?.uptime ? formatUptime(stat.uptime) : '–';
  const avgScanTime = stat?.scan_times?.filter(t => t)?.reduce((a,b,_,arr) => a + b/arr.length, 0) || 0;
  const blDomains = lists?.blacklist_domain || [];

  return (
    <>
      {toast && (
        <div style={{ position:'fixed', top:'1rem', right:'1rem', zIndex:9999, padding:'.7rem 1rem', borderRadius:8,
          background: toast.kind==='ok'?'rgba(16,185,129,.15)':'rgba(239,68,68,.15)',
          border:'1px solid '+(toast.kind==='ok'?'#10b981':'#ef4444'), fontSize:'.85rem', backdropFilter:'blur(8px)' }}>
          {toast.text}
        </div>
      )}

      <div className="page-title">
        <h1><ShieldAlert size={20} style={{verticalAlign:'middle'}}/> Rspamd · Anti-Spam</h1>
        <div>
          <span style={{color:'#a1a1aa', fontSize:'.8rem', marginRight:'.6rem'}}>auto-refresh 15s</span>
          <button onClick={load} className="ghost" disabled={loading}>
            <RefreshCw size={14}/> {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        </div>
      </div>

      {stat && (
        <div className="cards">
          <div className="card"><div className="label">Total escaneado</div><div className="value">{total.toLocaleString('pt-BR')}</div></div>
          <div className="card"><div className="label">Aceitos (no action)</div><div className="value ok">{passed.toLocaleString('pt-BR')}</div></div>
          <div className="card"><div className="label">Rejeitados (spam)</div><div className="value err">{rejected.toLocaleString('pt-BR')}</div></div>
          <div className="card"><div className="label">Bloqueios manuais</div><div className="value" style={{color: blDomains.length||((lists?.blacklist_ip||[]).length)?'#ef4444':undefined}}>{(blDomains.length + (lists?.blacklist_ip?.length||0)).toLocaleString('pt-BR')}</div></div>
          <div className="card"><div className="label">Uptime</div><div className="value" style={{fontSize:'1.1rem'}}>{uptime}</div></div>
          <div className="card"><div className="label">Versão Rspamd</div><div className="value" style={{fontSize:'1.1rem'}}>{stat.version}</div></div>
        </div>
      )}

      <div className="tab-bar">
        <div className={'tab ' + (tab==='overview'?'active':'')} onClick={()=>setTab('overview')}><Inbox size={14} style={{verticalAlign:'middle'}}/> Distribuição</div>
        <div className={'tab ' + (tab==='blocked'?'active':'')} onClick={()=>setTab('blocked')}><Ban size={14} style={{verticalAlign:'middle'}}/> Bloqueados</div>
        <div className={'tab ' + (tab==='lists'?'active':'')} onClick={()=>setTab('lists')}><ShieldCheck size={14} style={{verticalAlign:'middle'}}/> Whitelist / Blacklist</div>
        <div className={'tab ' + (tab==='history'?'active':'')} onClick={()=>setTab('history')}><Mail size={14} style={{verticalAlign:'middle'}}/> Histórico</div>
        <div className={'tab ' + (tab==='errors'?'active':'')} onClick={()=>setTab('errors')}><AlertOctagon size={14} style={{verticalAlign:'middle'}}/> Erros ({errors.length})</div>
        <div className={'tab ' + (tab==='actions'?'active':'')} onClick={()=>setTab('actions')}><Filter size={14} style={{verticalAlign:'middle'}}/> Thresholds</div>
      </div>

      {tab === 'overview' && (
        <>
          <h3>Distribuição por ação</h3>
          {Object.keys(actionsCount).length === 0 ? <p style={{color:'#a1a1aa'}}>Sem dados ainda.</p> : (
            <table>
              <thead><tr><th>Ação</th><th>Mensagens</th><th>% do total</th><th style={{width:'40%'}}>Distribuição</th></tr></thead>
              <tbody>
                {Object.entries(actionsCount).map(([name, count]) => {
                  const pct = total ? (count / total) * 100 : 0;
                  const meta = ACTION_COLORS[name] || { color: '#a1a1aa', label: name };
                  return (
                    <tr key={name}>
                      <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                      <td>{count.toLocaleString('pt-BR')}</td>
                      <td>{pct.toFixed(1)}%</td>
                      <td><div style={{background:'#27272a', borderRadius:'4px', overflow:'hidden', height:'12px'}}>
                        <div style={{background: meta.color, width: pct + '%', height:'100%'}}/>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {actions && (
            <>
              <h3 style={{marginTop:'1.5rem'}}>Estatísticas Bayes</h3>
              <div className="row" style={{gap:'1rem'}}>
                <div className="card" style={{flex:1}}><div className="label">Mensagens aprendidas</div><div className="value">{(stat?.total_learns || 0).toLocaleString('pt-BR')}</div></div>
                <div className="card" style={{flex:1}}><div className="label">Conexões ativas</div><div className="value">{stat?.connections || 0}</div></div>
                <div className="card" style={{flex:1}}><div className="label">Tempo médio de scan</div><div className="value">{(avgScanTime*1000).toFixed(0)} ms</div></div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'blocked' && (
        <>
          <h3><Ban size={16} style={{verticalAlign:'middle', color:'#ef4444'}}/> Domínios bloqueados</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>Domínios na blacklist manual + remetentes que foram rejeitados no tráfego real.</p>

          <h4 style={{marginTop:'1rem', color:'#e4e4e7'}}>Na blacklist manual ({blDomains.length})</h4>
          {blDomains.length === 0 ? <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>Nenhum domínio bloqueado manualmente. Use a aba <strong>Whitelist / Blacklist</strong> para adicionar.</p> : (
            <div style={{display:'flex', flexWrap:'wrap', gap:'.5rem', marginTop:'.5rem'}}>
              {blDomains.map(d => (
                <span key={d} className="badge err" style={{display:'inline-flex', alignItems:'center', gap:6, fontFamily:'JetBrains Mono'}}>
                  {d} <X size={12} style={{cursor:'pointer'}} onClick={()=>removeFromList('blacklist_domain', d)}/>
                </span>
              ))}
            </div>
          )}

          <h4 style={{marginTop:'1.5rem', color:'#e4e4e7'}}>Rejeitados recentemente no tráfego ({blocked.total})</h4>
          {blocked.domains.length === 0 ? <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>Nenhuma mensagem rejeitada no histórico recente. Aparecerá aqui quando o servidor receber e bloquear spam.</p> : (
            <table style={{marginTop:'.5rem'}}>
              <thead><tr><th>Domínio remetente</th><th>Rejeições</th><th></th></tr></thead>
              <tbody>
                {blocked.domains.map(d => (
                  <tr key={d.domain}>
                    <td style={{fontFamily:'JetBrains Mono'}}>{d.domain}</td>
                    <td><span className="badge err">{d.count}</span></td>
                    <td>{!blDomains.includes(d.domain) && <button className="ghost" style={{fontSize:'.75rem'}} onClick={()=>addToList('blacklist_domain', d.domain)}><Ban size={11}/> bloquear sempre</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'lists' && lists && (
        <>
          <p style={{color:'#a1a1aa', fontSize:'.85rem', marginBottom:'1rem'}}>
            <strong style={{color:'#10b981'}}>Whitelist</strong> = sempre aceitar (pula a verificação de spam). <strong style={{color:'#ef4444'}}>Blacklist</strong> = sempre rejeitar. Aplicado imediatamente no Rspamd.
          </p>
          <div className="row" style={{gap:'1rem', alignItems:'flex-start', flexWrap:'wrap'}}>
            <ListManager title="IPs liberados" sub="whitelist por IP/CIDR" kind="ip" tone="ok"
              items={lists.whitelist_ip} onAdd={v=>addToList('whitelist_ip', v)} onRemove={v=>removeFromList('whitelist_ip', v)} ph="203.0.113.5 ou 203.0.113.0/24"/>
            <ListManager title="IPs bloqueados" sub="blacklist por IP/CIDR" kind="ip" tone="err"
              items={lists.blacklist_ip} onAdd={v=>addToList('blacklist_ip', v)} onRemove={v=>removeFromList('blacklist_ip', v)} ph="203.0.113.66 ou 203.0.113.0/24"/>
          </div>
          <div className="row" style={{gap:'1rem', alignItems:'flex-start', flexWrap:'wrap', marginTop:'1rem'}}>
            <ListManager title="Domínios liberados" sub="whitelist por domínio remetente" kind="domain" tone="ok"
              items={lists.whitelist_domain} onAdd={v=>addToList('whitelist_domain', v)} onRemove={v=>removeFromList('whitelist_domain', v)} ph="parceiro.com.br"/>
            <ListManager title="Domínios bloqueados" sub="blacklist por domínio remetente" kind="domain" tone="err"
              items={lists.blacklist_domain} onAdd={v=>addToList('blacklist_domain', v)} onRemove={v=>removeFromList('blacklist_domain', v)} ph="spammer.com"/>
          </div>
        </>
      )}

      {tab === 'history' && (
        <>
          <h3>Últimas mensagens escaneadas</h3>
          {history.length === 0 ? <p style={{color:'#a1a1aa'}}>Nenhuma mensagem ainda. Quando o servidor receber emails, aparecem aqui.</p> : (
            <table>
              <thead><tr><th>Quando</th><th>De</th><th>Para</th><th>Score</th><th>Ação</th><th>Símbolos</th></tr></thead>
              <tbody>
                {history.slice(0, 50).map((m, i) => {
                  const meta = ACTION_COLORS[m.action] || { color: '#a1a1aa', label: m.action, icon: 'ok' };
                  const scoreColor = m.score < 5 ? '#10b981' : m.score < 10 ? '#f59e0b' : '#ef4444';
                  return (
                    <tr key={i}>
                      <td style={{color:'#a1a1aa', fontSize:'.78rem', whiteSpace:'nowrap'}}>{new Date(m.unix_time * 1000).toLocaleString('pt-BR')}</td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>{m.from_mime || m.from_smtp || '-'}</td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>{(m.rcpt_mime || m.rcpt_smtp || []).join(', ').slice(0, 40)}</td>
                      <td style={{color: scoreColor, fontWeight:'600'}}>{m.score?.toFixed(2)}</td>
                      <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.7rem', maxWidth:'350px'}}>
                        {(m.symbols ? (Array.isArray(m.symbols) ? m.symbols.map(s=>s.name||s) : Object.keys(m.symbols)) : []).slice(0,5).join(', ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'errors' && (
        <>
          <h3>Erros recentes</h3>
          {errors.length === 0 ? <p style={{color:'#10b981'}}>✓ Sem erros — Rspamd operando normalmente.</p> : (
            <table>
              <thead><tr><th>Quando</th><th>Tipo</th><th>Mensagem</th></tr></thead>
              <tbody>
                {errors.slice(0, 50).map((e, i) => (
                  <tr key={i}>
                    <td style={{color:'#a1a1aa', fontSize:'.78rem'}}>{new Date((e.ts || 0) * 1000).toLocaleString('pt-BR')}</td>
                    <td><span className="badge err">{e.type}</span></td>
                    <td style={{fontSize:'.82rem', fontFamily:'JetBrains Mono'}}>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'actions' && (
        <>
          <h3>Thresholds (limites de pontuação)</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>Cada email recebe uma pontuação de "spamicidade". Ao atingir um threshold, a ação correspondente é aplicada. <em>—</em> = sem limite definido (ação não usada por score).</p>
          {actions ? (() => {
            const rows = Array.isArray(actions)
              ? actions.map(a => ({ name: a.action, value: a.value }))
              : Object.entries(actions || {}).map(([name, value]) => ({ name, value }));
            return (
              <table>
                <thead><tr><th>Ação</th><th>Threshold (score)</th><th>O que faz</th></tr></thead>
                <tbody>
                  {rows.map(({ name, value }) => {
                    const meta = ACTION_COLORS[name] || { label: name, icon: 'ok' };
                    const num = typeof value === 'number' ? value.toFixed(2) : (value == null ? '—' : String(value));
                    const desc = {
                      'no action': 'Entrega normal na caixa de entrada',
                      'greylist': 'Adia temporariamente (anti-spam leve)',
                      'add header': 'Marca como suspeito (vai pra Spam)',
                      'rewrite subject': 'Reescreve o assunto avisando',
                      'soft reject': 'Recusa temporária (tente mais tarde)',
                      'reject': 'Rejeita definitivamente o email',
                    }[name] || '';
                    return (
                      <tr key={name}>
                        <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                        <td style={{fontFamily:'JetBrains Mono'}}>{num}</td>
                        <td style={{color:'#a1a1aa', fontSize:'.82rem'}}>{desc}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })() : <p style={{color:'#a1a1aa'}}>Carregando…</p>}
          <div style={{marginTop:'1rem', padding:'.8rem 1rem', background:'rgba(16,185,129,.08)', border:'1px solid #10b98144', borderRadius:8, fontSize:'.85rem'}}>
            <ShieldCheck size={15} style={{verticalAlign:'middle', color:'#10b981'}}/> <strong>Seus domínios estão protegidos:</strong> faarnet.com.br, powerturbonet.com.br e esstelecom.com.br estão na whitelist — emails enviados por eles nunca são bloqueados. Gerencie na aba <strong>Whitelist / Blacklist</strong>.
          </div>
        </>
      )}
    </>
  );
}

function ListManager({ title, sub, kind, tone, items, onAdd, onRemove, ph }) {
  const [v, setV] = useState('');
  const accent = tone === 'ok' ? '#10b981' : '#ef4444';
  const Icon = tone === 'ok' ? ShieldCheck : ShieldX;
  const submit = () => { onAdd(v); setV(''); };
  return (
    <div className="card" style={{ flex:1, minWidth:320, borderColor: accent+'44' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'.2rem' }}>
        <Icon size={16} color={accent}/>
        <div><div style={{ fontWeight:600 }}>{title}</div><div style={{ fontSize:'.72rem', color:'#a1a1aa' }}>{sub}</div></div>
        <span className="badge" style={{ marginLeft:'auto', background:accent+'22', color:accent }}>{items.length}</span>
      </div>
      <div style={{ display:'flex', gap:6, margin:'.6rem 0' }}>
        <input value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}
          placeholder={ph} style={{ flex:1, padding:'6px 10px', fontSize:'.82rem', fontFamily:'JetBrains Mono',
          background:'#0a0a0a', border:'1px solid #27272a', borderRadius:6, color:'#e4e4e7' }}/>
        <button onClick={submit} style={{ background:accent, border:'none', borderRadius:6, color:'#fff', padding:'0 12px', cursor:'pointer' }}><Plus size={14}/></button>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize:'.78rem', color:'#71717a', padding:'.4rem 0' }}>Lista vazia.</div>
      ) : (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:240, overflow:'auto' }}>
          {items.map(it => (
            <span key={it} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 8px', borderRadius:6,
              background:'#18181b', border:'1px solid #27272a', fontFamily:'JetBrains Mono', fontSize:'.78rem' }}>
              {it}
              <X size={12} style={{ cursor:'pointer', color:'#a1a1aa' }} onClick={()=>onRemove(it)}/>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatUptime(s) {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
